/**
 * Automation Module - Tasks Automation Hook
 *
 * Orchestrates task CRUD state and delegates to service hooks.
 */

import { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { ToastMessageContext } from '@context/toast-message-cotnext';
import { useGetAllTasks } from '../services/tasks.queries';
import { useDeleteTask } from '../services/tasks.mutations';
import type { IAutomationItem } from '../types';

export const useTasksAutomation = () => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useContext(ToastMessageContext);

  const [activePage, setActivePage] = useState(1);
  const [perPage, setPerPage] = useState('20');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<IAutomationItem | null>(null);

  // Queries
  const {
    data: tasksData,
    isLoading: tasksLoading,
    isError: tasksError,
    refetch: refetchTasks,
  } = useGetAllTasks({
    pageSize: Number(perPage),
    pageNumber: activePage,
  });

  // Mutations
  const deleteTaskMutation = useDeleteTask();

  // Actions
  const openCreateModal = () => {
    setCreateModalOpen(true);
  };

  const closeCreateModal = () => {
    setCreateModalOpen(false);
  };

  const openEditModal = (task: IAutomationItem) => {
    setSelectedTask(task);
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setSelectedTask(null);
  };

  const openDeleteDialog = (task: IAutomationItem) => {
    setSelectedTask(task);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedTask(null);
  };

  const handleDeleteTask = async () => {
    if (!selectedTask) return;
    try {
      const response = await deleteTaskMutation.mutateAsync({ id: selectedTask.id });
      if (response.isSuccess) {
        showSuccess(t('Automation.taskDeletedSuccessfully'));
        closeDeleteDialog();
        refetchTasks();
      } else {
        showError(response.message || t('ERRORS.SOMETHING_WENT_WRONG'));
      }
    } catch {
      showError(t('ERRORS.SOMETHING_WENT_WRONG'));
    }
  };

  return {
    // State
    activePage,
    setActivePage,
    perPage,
    setPerPage,
    createModalOpen,
    editModalOpen,
    deleteDialogOpen,
    selectedTask,

    // Data
    tasks: tasksData?.tasks ?? [],
    totalRecords: tasksData?.totalRecords ?? 0,
    tasksLoading,
    tasksError,

    // Mutation loading states
    isDeleting: deleteTaskMutation.isPending,

    // Actions
    openCreateModal,
    closeCreateModal,
    openEditModal,
    closeEditModal,
    openDeleteDialog,
    closeDeleteDialog,
    handleDeleteTask,
    refetchTasks,
  };
};
